import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanMatchFn, Route, UrlSegment } from '@angular/router';
import { filter, map, take } from 'rxjs';
import { CATALOGO_SERVICOS } from '../../../features/portal-carta-servico/pages-servicos/pages-servicos.component';
import { CatalogoPublicoService } from '../../../features/portal-carta-servico/services/catalogo-publico.service';
import { CategoriaServicosService } from '../../../features/portal-carta-servico/services/categoria-servicos.service';

/**
 * A rota `:rotaPagina` é curinga: sem esta checagem ela casaria com qualquer endereço e o portal
 * abriria uma página de serviço vazia no lugar do 404. Recusando o casamento, o roteador segue
 * para a rota `**`, que mostra a tela de página não encontrada.
 *
 * São três fontes de rota válida: os dois catálogos ESTÁTICOS históricos e, desde que a tela
 * "Todos os Serviços" passou a vir da API, os serviços PUBLICADOS — cuja rota é o slug do nome
 * (ver `slugDeServico`). Sem a terceira fonte, o "Acesse aqui" de um serviço cadastrado no adm
 * cairia no 404.
 *
 * A lista de "Serviços Digitais" não é fonte própria: os cards dela são serviços publicados, e
 * já passam pela terceira. Os oito exemplos que ela tinha (`/financas`…) seguem no grupo
 * "Serviços digitais" de `CategoriaServicosService`.
 *
 * A terceira fonte é assíncrona: o catálogo público só começa a carregar quando alguém injeta o
 * serviço, e um link direto (favorito, ou o card da busca, que não carrega o catálogo) chega
 * antes da resposta. Nesse caso o guard **espera** o catálogo assentar antes de decidir — o
 * roteador aceita `Observable<boolean>` — em vez de responder 404 para um serviço que existe.
 */
export const servicoExisteGuard: CanMatchFn = (_rota: Route, segmentos: UrlSegment[]) => {
  const rota = segmentos[0]?.path ?? '';

  if (!rota) return false;

  if (rota in CATALOGO_SERVICOS) return true;

  const categorias = inject(CategoriaServicosService).categoriaServicos();

  if (categorias.some((categoria) => categoria.rotaPagina === rota)) return true;

  // Os serviços do catálogo público. O `link` já é o slug com a barra à frente, então a
  // comparação é com a rota crua — sem recalcular o slug e arriscar divergir de quem o gerou.
  const catalogo = inject(CatalogoPublicoService);
  const existeNoCatalogo = () =>
    catalogo.categorias().some((categoria) => categoria.servicos.some((servico) => servico.link === `/${rota}`));

  if (!catalogo.carregando()) return existeNoCatalogo();

  return toObservable(catalogo.carregando).pipe(
    filter((carregando) => !carregando),
    take(1),
    map(existeNoCatalogo),
  );
};
